import {
  SubmitPayOverTimeEmploymentInfoDataInput,
  SubmitPayOverTimeOnboardingPaymentPlan,
  SubmitPayOverTimeOnboardingResult,
  SubmitPayOverTimeOnboardingResultTypenames,
} from '@varo/graphql-schema-types';
import {
  buildErrorModel,
  type ErrorModel,
  logPayOverTimeError,
  resolveErrorCopy,
} from 'features/payOverTime/helpers/PayOverTime.helpers';
import { useRef } from 'react';
import SubmitPayOverTimeOnboardingStep from 'services/graphql/mutations/SubmitPayOverTimeOnboardingStep';
import { useDefaultErrorStrings } from 'shared/hooks/errorFormatters/useDefaultError/useDefaultErrorStrings';
import useCancellableMutation, {
  AbortError,
} from 'shared/hooks/useCancellableMutation/useCancellableMutation';
import { getUuid } from 'shared/utils/getUuid/getUuid';

export enum PayOverTimeOnboardingStatus {
  PaymentPlan = 'PaymentPlan',
  Declined = 'Declined',
  Error = 'Error',
  Cancelled = 'Cancelled',
}

export type PayOverTimeOnboardingOutcome =
  | {
      status: PayOverTimeOnboardingStatus.PaymentPlan;
      paymentPlan: SubmitPayOverTimeOnboardingPaymentPlan;
    }
  | { status: PayOverTimeOnboardingStatus.Declined }
  | {
      status: PayOverTimeOnboardingStatus.Error;
      errorModel: ErrorModel;
    }
  | { status: PayOverTimeOnboardingStatus.Cancelled };

const CANCELLED: PayOverTimeOnboardingOutcome = {
  status: PayOverTimeOnboardingStatus.Cancelled,
};

const failed = (errorModel: ErrorModel): PayOverTimeOnboardingOutcome => ({
  status: PayOverTimeOnboardingStatus.Error,
  errorModel,
});

const useSubmitPayOverTimeOnboardingStep = () => {
  const { defaultTitle, defaultMessage } = useDefaultErrorStrings();
  const [{ fetching }, executeMutation, abortCurrentMutation] =
    useCancellableMutation<{
      submitPayOverTimeOnboardingStep: SubmitPayOverTimeOnboardingResult;
    }>(SubmitPayOverTimeOnboardingStep);
  const hasInFlightSubmitRef = useRef(false);

  const cancelOnboardingStep = () => {
    if (!hasInFlightSubmitRef.current) {
      return;
    }

    abortCurrentMutation();
  };

  const submitOnboardingStep = async (input: {
    transactionId: string;
    employmentInfo?: SubmitPayOverTimeEmploymentInfoDataInput;
  }): Promise<PayOverTimeOnboardingOutcome> => {
    if (hasInFlightSubmitRef.current) {
      return CANCELLED;
    }

    hasInFlightSubmitRef.current = true;

    const { transactionId, employmentInfo } = input;

    try {
      const response = await executeMutation({
        idempotencyId: getUuid(),
        input: {
          transactionId,
          employmentInfo: employmentInfo ?? null,
        },
      });

      const result = response.data?.submitPayOverTimeOnboardingStep;

      if (
        result?.__typename ===
        SubmitPayOverTimeOnboardingResultTypenames.SubmitPayOverTimeOnboardingPaymentPlan
      ) {
        return {
          status: PayOverTimeOnboardingStatus.PaymentPlan,
          paymentPlan: result,
        };
      }

      if (
        result?.__typename ===
        SubmitPayOverTimeOnboardingResultTypenames.SubmitPayOverTimeOnboardingApplicationDeclined
      ) {
        return { status: PayOverTimeOnboardingStatus.Declined };
      }

      if (
        result?.__typename ===
        SubmitPayOverTimeOnboardingResultTypenames.SubmitPayOverTimeOnboardingError
      ) {
        logPayOverTimeError(new Error(result.message), {
          transactionId,
          code: result.code,
          typename: result.__typename,
        });
        return failed(
          buildErrorModel(
            resolveErrorCopy(result.title, defaultTitle),
            resolveErrorCopy(result.message, defaultMessage),
          ),
        );
      }

      if (
        result?.__typename ===
        SubmitPayOverTimeOnboardingResultTypenames.SubmitPayOverTimeOnboardingEmploymentTypes
      ) {
        logPayOverTimeError(
          new Error(
            'Pay Over Time onboarding returned employment types, which the app no longer collects',
          ),
          { transactionId, typename: result.__typename },
        );
        return failed(buildErrorModel(defaultTitle, defaultMessage));
      }

      logPayOverTimeError(
        response.error ??
          new Error('Pay Over Time onboarding returned no result'),
        { transactionId, typename: result?.__typename ?? null },
      );
      return failed(buildErrorModel(defaultTitle, defaultMessage));
    } catch (caught) {
      if (caught instanceof AbortError) {
        return CANCELLED;
      }

      logPayOverTimeError(caught, { transactionId });
      return failed(buildErrorModel(defaultTitle, defaultMessage));
    } finally {
      hasInFlightSubmitRef.current = false;
    }
  };

  return { submitOnboardingStep, cancelOnboardingStep, fetching };
};

export default useSubmitPayOverTimeOnboardingStep;
